import { RootState } from "common/reducers";
import { createSelector } from "reselect";
import { getRecordsByModel } from "common/selectors/idsManagement.selectors";
import { DateFormat } from "common/helpers/Format";
import { JobRecord, validMetadataIndices } from "common/records/Job";
import { DocumentRecord, IDocumentData } from "common/records/Document";
import { getPrettyName } from "common/helpers/Pretty";
import { currentOrgJobStateRecord } from "common/selectors/org.selectors";
import { fromJSON as dropdownFromJSON, DropdownRecord } from "common/components/Dropdown/Dropdown";
import { List } from "immutable";
import { Nullable } from "common/types";

export const rootKey = "jobs";

/**
 * Gets the job ID currently set as 'current'
 *
 * @param {RootState} state The RootState
 * @param {{}} props
 * @returns number
 */
export const currentJobId = (state: RootState, _props?: any) =>
  state.getIn([rootKey, "currentJobId"]) as Nullable<number>;

/**
 * Loads the current job using currentJobId
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {JobRecord} JobRecord
 */
export const currentJob = (state: RootState, _props?: any) => {
  const id = currentJobId(state);
  if (!id) {
    return null;
  }
  return state.getIn([rootKey, "byId"]).get(id);
};

/**
 * Loads the current job name
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {string} string
 */
export const currentJobName = (state: RootState) => currentJob(state)?.get("name");

/**
 * Loads the current job measurement_id
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {string} string
 */
export const currentJobMeasurementId = (state: RootState) => currentJob(state)?.get("measurement_id");

/**
 * Loads the current job errors
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {string} string
 */
export const currentJobErrors = (state: RootState) => currentJob(state)?.get("errors");

/**
 * Loads the list of sample jobs
 */
export const sampleJobs = (state: RootState) => state.getIn([rootKey, "sampleJobs"]);

/**
 * Loads the resource documents list for the current job
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {List<any>} List of records
 */
export const resourceDocuments = (state: RootState, _props?: any) =>
  state.getIn(["jobs", "byId", currentJobId(state), "config", "resource_documents"], List()) as List<any>;

/**
 * Loads the resource documents ids list for the current job
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {List<number>} List of ids
 */
export const jobResourceIds = createSelector([resourceDocuments], (documents) =>
  documents.map((doc: DocumentRecord) => doc.get("id")),
);

/**
 * Loads the current job using currentJobId and returns job.loading
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {boolean} loading
 */
export const currentJobLoading = createSelector([currentJob], (job) => {
  if (!job) return true;

  return job.loading;
});

/**
 * Loads the list of job overviews using the idsManagement `getRecordsByModel` selector
 */
export const jobOverviews = (state: RootState) =>
  getRecordsByModel(state, {
    modelName: "jobs",
    path: ["jobs", "overviewsById"],
    includeArchived: true,
  });

/** Neutral color fallback for a job whose status slug no longer resolves (hard-deleted from job_states). */
export const UNKNOWN_JOB_STATE_COLOR = "#9CA3AF";

/** Falls back to a neutral color for a hard-deleted slug (no resolving jobStates entry). */
export const jobStateColor = (state: RootState, id: string): string =>
  state
    .getIn([rootKey, "jobStates"])
    .find((state) => state.get("id") === id)
    ?.get("color") ?? UNKNOWN_JOB_STATE_COLOR;

/** Resolves a status slug to the org's configured pretty_name, falling back to the humanized slug for a renamed/hard-deleted status. */
export const jobStateLabel = (state: RootState, id: string): string =>
  currentOrgJobStateRecord(state).state_info.get(id)?.get("pretty_name") ?? getPrettyName(id);

/**
 * Change-Status dropdown options for the current job: each transition event (`on`) is labeled with its
 * target status's configured pretty_name, falling back to the humanized slug when the target has no entry
 * or the event matches no rule. The option value stays the raw `on` event (display-only relabel).
 */
export const jobStatusChangeOptions = createSelector(
  [currentJob, currentOrgJobStateRecord],
  (job, jobState): List<DropdownRecord> => {
    const rawState = job?.raw_state ?? "";
    return (job?.events ?? List<string>()).map((on) => {
      const toSlug = jobState.transitions.find((t) => t.on === on && t.from.includes(rawState))?.to;
      const label = toSlug
        ? (jobState.state_info.get(toSlug)?.get("pretty_name") ?? getPrettyName(toSlug))
        : getPrettyName(on);
      return dropdownFromJSON({ id: on, name: label });
    });
  },
);

/**
 * Current job's status label: the org's configured pretty_name for its slug, falling back to the job's
 * humanized display `state` (distinct from jobStateLabel, which falls back to the humanized slug).
 */
export const currentJobStatusLabel = createSelector(
  [currentJob, currentOrgJobStateRecord],
  (job, jobState): string =>
    (job?.raw_state ? jobState.state_info.get(job.raw_state)?.get("pretty_name") : undefined) || job?.state || "",
);

/**
 * Loads the current jobParams used for listJobs
 *
 * @param {RootState} state The RootState
 * @param jobParamsType selects the jobParams or orgJobParams slice
 */
export const jobParams = (state: RootState, jobParamsType: "jobParams" | "orgJobParams") =>
  state.getIn([rootKey, jobParamsType]);

/**
 * Loads the list of job overviews using the idsManagement `getRecordsByModel` selector
 */
export const jobOverviewById = (state: RootState, jobId: number) => state.getIn([rootKey, "overviewsById"]).get(jobId);

/**
 * Gets the start time for the upcoming appointment
 * @param {RootState} state The RootState
 * @param {{jobId: number}} options
 * @returns {string} No Appt. or formatted start time
 */

export const nextAppointment = (state: RootState, jobId: number) => {
  const formattedStartTime = DateFormat(jobOverviewById(state, jobId)?.appointments.first()?.get("start_time"));

  if (formattedStartTime === "Invalid date") return "No Appt.";

  return formattedStartTime;
};

/**
 * Gets the name of the user to whom the job is assigned
 * @param {RootState} state The RootState
 * @param {{jobId: number}} options
 * @returns {string} Full name of user
 */

export const assignedTo = (state: RootState, jobId: number) => {
  const assignedUser = jobOverviewById(state, jobId)?.assignments.first()?.user;

  if (assignedUser) return assignedUser.get("first_name") + " " + assignedUser.get("last_name");

  return "";
};

/**
 * The user id assigned to the current job (its first assignment), or undefined when unassigned.
 * The Commission Calculator computes commission from THIS user's access config (overhead /
 * commission_rate), not the current user's (RSF-25820 BR-6).
 *
 * @param {RootState} state The RootState
 * @returns {number | undefined} the assigned user's id, or undefined when unassigned
 */
export const jobAssignedUserId = (state: RootState): number | undefined =>
  currentJob(state)?.assignments?.get(0)?.user_id;

/**
 * Loads the current job's root folder id by name
 *
 * @param {RootState} state The RootState
 * @param {{ folderName }} options
 * @returns {number} number
 */
export const currentJobRootFolderId = createSelector([currentJob, (_state, props) => props], (job, props) => {
  return job?.getIn(["folders", props.folderName]);
});

/**
 * Checks if job has integration key and value set in its metadata
 * @param {{job: JobRecord, integrationKey: string}}
 * @returns boolean value for its existence
 */
export const hasIntegration = (job: JobRecord, integrationKey: string): boolean => {
  const metadata = job?.metadata;

  if (!metadata) return false;

  return validMetadataIndices.some(
    (idx) =>
      metadata.get("integration_key_" + idx)?.includes(integrationKey) &&
      metadata.get("integration_value_" + idx) !== undefined,
  );
};
